
import React from 'react';
import { ShieldCheck, X, CheckCircle2, AlertTriangle } from 'lucide-react';

interface TermsModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onClose?: () => void;
  isDarkMode: boolean;
  isSaving?: boolean;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onAccept, onClose, isDarkMode, isSaving = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-[32px] shadow-2xl overflow-hidden border animate-in zoom-in-95 duration-300 ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-gray-200'}`}>
        
        {/* Header */}
        <div className={`px-8 py-6 border-b flex justify-between items-center shrink-0 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 p-2.5 rounded-2xl shadow-lg shadow-blue-600/20">
              <ShieldCheck size={24} className="text-white" />
            </div>
            <div>
              <h2 className={`text-xl font-black uppercase tracking-tight ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Términos y Condiciones
              </h2>
              <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mt-0.5">
                Plataforma Profesional MODULAR
              </p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
              <X size={20} className="text-gray-400" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          <div className={`space-y-6 text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            
            <section>
              <h3 className="text-xs font-black uppercase text-blue-500 mb-2 tracking-widest">1. IDENTIFICACIÓN DEL RESPONSABLE</h3>
              <p>El presente documento regula el acceso y uso del sitio web (en adelante, la “Plataforma”), cuyo titular es la entidad responsable del servicio (en adelante, el “Responsable del Tratamiento”), conforme a lo establecido en el Reglamento (UE) 2016/679 (RGPD).</p>
            </section>

            <section>
              <h3 className="text-xs font-black uppercase text-blue-500 mb-2 tracking-widest">2. OBJETO DEL SERVICIO</h3>
              <p>La Plataforma proporciona herramientas tecnológicas destinadas a:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Transcripción de dictados médicos</li>
                <li>Estructuración de informes clínicos</li>
                <li>Optimización lingüística y técnica de informes radiológicos</li>
              </ul>
              <p className="mt-2 font-bold text-red-500">La Plataforma no constituye un sistema de historia clínica electrónica ni un repositorio de datos clínicos.</p>
            </section>

            <section>
              <h3 className="text-xs font-black uppercase text-blue-500 mb-2 tracking-widest">3. CONDICIONES DE ACCESO</h3>
              <p>El acceso a la Plataforma está restringido a médicos colegiados, radiólogos y profesionales sanitarios cualificados.</p>
            </section>

            <div className={`p-5 rounded-2xl border-2 border-dashed ${isDarkMode ? 'bg-red-900/10 border-red-900/30' : 'bg-red-50 border-red-100'}`}>
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-red-500 shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="text-xs font-black uppercase text-red-600 mb-2 tracking-widest">4. PROTECCIÓN DE DATOS PERSONALES (CRÍTICO)</h3>
                  <p className="text-xs font-bold text-red-700 dark:text-red-400">NO está permitido el tratamiento de datos personales de pacientes dentro de la Plataforma.</p>
                  <p className="text-[11px] mt-2 leading-relaxed">Queda terminantemente prohibido introducir nombres, apellidos, DNI, número de historia clínica o cualquier dato que permita la identificación directa o indirecta del paciente. El usuario debe garantizar la anonimización irreversible.</p>
                </div>
              </div>
            </div>

            <section>
              <h3 className="text-xs font-black uppercase text-blue-500 mb-2 tracking-widest">5. RESPONSABILIDAD DEL USUARIO</h3>
              <p>El usuario es responsable exclusivo del contenido introducido y del cumplimiento del RGPD.</p>
            </section>

            <section>
              <h3 className="text-xs font-black uppercase text-blue-500 mb-2 tracking-widest">14. MONITORIZACIÓN Y AUSENCIA DE PRIVACIDAD</h3>
              <p>Las comunicaciones NO son privadas frente al sistema. La Plataforma analiza automáticamente los contenidos para detectar nombres o datos identificadores con la finalidad de prevenir incumplimientos y garantizar el cumplimiento del RGPD.</p>
              <p className="mt-2 font-bold">El sistema puede bloquear contenido o suspender usuarios si detecta infracciones.</p>
            </section>

            <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
              <p className="text-[11px] italic opacity-70">Cláusula final: La negativa a aceptar estas condiciones impide el uso de la Plataforma.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`px-8 py-6 border-t flex flex-col sm:flex-row gap-4 items-center justify-between shrink-0 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
          <div className="flex items-center space-x-2 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 size={16} />
            <span className="text-[10px] font-black uppercase tracking-widest">Consentimiento Requerido</span>
          </div>
          <button
            onClick={onAccept}
            disabled={isSaving}
            className={`w-full sm:w-auto px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-blue-600/20 transition-all active:scale-95 flex items-center justify-center space-x-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <span>Aceptar y Continuar</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
